/**
 * ExamSubmissionsPanel — Post-submission workflow UI.
 *
 * Two views in one component:
 *   1. List view — table of every submission for the chosen class+subject,
 *      with status, score, letter grade and competency band.
 *   2. Marking view — full-card review of a single student's answers,
 *      with per-question score + feedback inputs and a single "Save &
 *      finalize" action that batches all answers in one request.
 *
 * Wires to backend endpoints that already exist in school_exams.py:
 *   GET   /school/tenants/{tid}/exams/{eid}/submissions?class_id&subject_id
 *   GET   /school/tenants/{tid}/exams/{eid}/subjects/{sid}/questions
 *   POST  /school/tenants/{tid}/exams/{eid}/auto-mark?class_id&subject_id
 *   POST  /school/tenants/{tid}/exams/{eid}/manual-mark?subject_id
 *   POST  /school/tenants/{tid}/exams/{eid}/publish-results
 *   POST  /school/tenants/{tid}/exams/{eid}/unpublish-results
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Loader2, ClipboardList, CheckCircle2, Clock, AlertCircle, Pencil,
  Send, EyeOff, RotateCcw, ArrowLeft, Sparkles, Award,
} from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const api = async (path, opts = {}) => {
  const r = await fetch(`${API}/api/school${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getToken()}`, ...(opts.headers || {}) },
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(e.detail || `HTTP ${r.status}`);
  }
  return r.json();
};

const STATUS_BADGE = {
  submitted:           { tone: 'bg-blue-100 text-blue-700',       icon: ClipboardList, label: 'Submitted' },
  auto_marked:         { tone: 'bg-purple-100 text-purple-700',   icon: CheckCircle2,  label: 'Auto-marked' },
  awaiting_manual:     { tone: 'bg-amber-100 text-amber-700',     icon: Clock,         label: 'Awaiting manual' },
  partially_marked:    { tone: 'bg-amber-100 text-amber-700',     icon: Clock,         label: 'Partially marked' },
  fully_marked:        { tone: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2,  label: 'Fully marked' },
  moderated:           { tone: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2,  label: 'Moderated' },
  published:           { tone: 'bg-green-100 text-green-800',     icon: Send,          label: 'Published' },
  voided:              { tone: 'bg-slate-100 text-slate-500',     icon: AlertCircle,   label: 'Voided' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_BADGE[status] || { tone: 'bg-slate-100 text-slate-600', icon: ClipboardList, label: status || 'Unknown' };
  const Icon = cfg.icon;
  return <Badge className={`${cfg.tone} gap-1 whitespace-nowrap`}><Icon className="h-3 w-3" />{cfg.label}</Badge>;
};

const GradeBadge = ({ grade, percentage, competency }) => {
  if (!grade && percentage == null) return <span className="text-xs text-slate-400">—</span>;
  return (
    <div className="flex flex-col items-end gap-0.5">
      {grade && <Badge variant="outline" className="font-bold text-xs">{grade}</Badge>}
      {percentage != null && <span className="text-[10px] text-slate-500">{percentage}%</span>}
      {competency && <span className="text-[10px] text-indigo-600 font-medium">{competency}</span>}
    </div>
  );
};

// ============================== MARKING VIEW ==============================
// Full-page (within-tab) marking surface — replaces the table when the
// teacher selects a single submission.
const MarkingView = ({ submission, exam, tenantId, subjectId, onBack, onSaved }) => {
  // Seed inputs from any existing graded_answers so the teacher sees prior
  // scores and feedback when reopening. Parent passes a `key` per submission,
  // which guarantees a fresh state on every selection — no effect needed.
  const initial = useMemo(() => {
    const s = {}, f = {};
    (submission.graded_answers || []).forEach(g => {
      if (g.score != null) s[g.question_id] = g.score;
      if (g.feedback) f[g.question_id] = g.feedback;
    });
    return { s, f };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [scores, setScores] = useState(initial.s);
  const [feedback, setFeedback] = useState(initial.f);
  const [moderated, setModerated] = useState(false);

  const { data: questions = [], isLoading: loadingQ } = useQuery({
    queryKey: ['exam-marking-questions', exam.id, subjectId],
    queryFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/subjects/${subjectId}/questions`),
    enabled: !!subjectId,
  });

  const answers = useMemo(() => {
    const map = {};
    (submission.answers || []).forEach(a => { map[a.question_id] = a; });
    return map;
  }, [submission]);
  const gradedMap = useMemo(() => {
    const map = {};
    (submission.graded_answers || []).forEach(g => { map[g.question_id] = g; });
    return map;
  }, [submission]);

  const saveMutation = useMutation({
    mutationFn: ({ answers, moderated }) => api(
      `/tenants/${tenantId}/exams/${exam.id}/manual-mark?subject_id=${subjectId}`,
      { method: 'POST', body: JSON.stringify({ submission_id: submission.id || submission._id, answers, moderated }) },
    ),
    onSuccess: (r) => {
      toast.success(`Saved · Total ${r.total_score} (${r.percentage}% · ${r.grade}${r.competency ? ' · ' + r.competency : ''})`);
      onSaved?.();
    },
    onError: e => toast.error(e.message),
  });

  const saveAll = (finalize) => {
    const payload = questions
      .filter(q => ['short_answer', 'long_essay'].includes(q.question_type))
      .map(q => ({
        question_id: q.id,
        score: parseFloat(scores[q.id] || 0),
        feedback: feedback[q.id] || '',
      }));
    if (payload.length === 0) {
      toast.info('No subjective questions to mark on this paper.');
      return;
    }
    saveMutation.mutate({ answers: payload, moderated: finalize && moderated });
  };

  const totalSoFar = useMemo(() => {
    return questions.reduce((sum, q) => {
      const manual = scores[q.id];
      if (['short_answer', 'long_essay'].includes(q.question_type) && manual != null && manual !== '') {
        return sum + (parseFloat(manual) || 0);
      }
      const auto = gradedMap[q.id]?.score;
      return sum + (auto || 0);
    }, 0);
  }, [questions, scores, gradedMap]);
  const totalMax = useMemo(() => questions.reduce((s, q) => s + (q.marks || 1), 0), [questions]);

  return (
    <div className="space-y-4" data-testid="marking-view">
      <div className="flex items-start justify-between">
        <div>
          <button onClick={onBack} className="text-xs text-indigo-600 hover:underline flex items-center gap-1 mb-1" data-testid="marking-back-btn">
            <ArrowLeft className="h-3 w-3" /> Back to submissions
          </button>
          <h3 className="text-lg font-bold">{submission.student_name || submission.student_id}</h3>
          <p className="text-xs text-slate-500">
            {exam.name} · {submission.admission_number || '—'} · Submitted {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : '—'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-slate-400">Running total</p>
          <p className="text-2xl font-bold text-indigo-700" data-testid="marking-total-score">{totalSoFar} <span className="text-sm text-slate-400">/ {totalMax}</span></p>
          <StatusBadge status={submission.marking_status} />
        </div>
      </div>

      {loadingQ ? (
        <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : questions.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-400">No questions on this paper.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {questions.map((q, idx) => {
            const ans = answers[q.id] || {};
            const auto = gradedMap[q.id] || {};
            const isObjective = ['mcq_single', 'mcq_multi', 'true_false', 'mcq'].includes(q.question_type);
            return (
              <Card key={q.id} className="border-l-4 border-l-indigo-200" data-testid={`marking-question-${q.id}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                        Q{idx + 1} · {q.question_type.replace('_', ' ')} · Max {q.marks}
                      </p>
                      <p className="text-sm font-medium mt-0.5 whitespace-pre-wrap">{q.question_text}</p>
                      {q.options?.length > 0 && (
                        <ul className="text-xs text-slate-500 mt-1 ml-3 list-disc">
                          {q.options.map((o, i) => <li key={i}>{o}{(q.correct_answer === o || q.correct_answers?.includes(o)) && <span className="text-emerald-600 ml-1">✓</span>}</li>)}
                        </ul>
                      )}
                      {!isObjective && q.correct_answer && (
                        <p className="text-xs text-slate-500 italic mt-1">Model answer: {q.correct_answer}</p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      {isObjective ? (
                        <Badge className="bg-purple-100 text-purple-700"><Sparkles className="h-3 w-3 mr-1" />Auto</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700">Manual</Badge>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded p-2 border border-slate-100">
                    <p className="text-[10px] text-slate-400 uppercase">Student answer</p>
                    <p className="text-sm whitespace-pre-wrap" data-testid={`marking-student-answer-${q.id}`}>
                      {ans.answer || <span className="text-slate-400 italic">(no answer)</span>}
                    </p>
                  </div>

                  {isObjective ? (
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-slate-500">{auto.feedback || 'Awaiting auto-mark run'}</span>
                      <Badge className={auto.score === q.marks ? 'bg-emerald-100 text-emerald-700' : auto.score > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}>
                        {auto.score ?? 0} / {q.marks}
                      </Badge>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-2 mt-2">
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Score (/ {q.marks})</Label>
                        <Input type="number" min={0} max={q.marks} step="0.5"
                               value={scores[q.id] ?? ''}
                               onChange={e => setScores(s => ({ ...s, [q.id]: e.target.value }))}
                               data-testid={`marking-score-${q.id}`}
                               className="h-9" />
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase text-slate-500">Feedback (visible to student)</Label>
                        <Textarea rows={2} value={feedback[q.id] ?? ''}
                                  onChange={e => setFeedback(f => ({ ...f, [q.id]: e.target.value }))}
                                  data-testid={`marking-feedback-${q.id}`}
                                  placeholder="Optional remarks…" />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {questions.some(q => ['short_answer', 'long_essay'].includes(q.question_type)) && (
        <Card className="bg-indigo-50/50 border-indigo-200">
          <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={moderated} onCheckedChange={setModerated} data-testid="marking-moderated-toggle" />
              Mark as moderated (HOD/principal review pass)
            </label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => saveAll(false)} disabled={saveMutation.isPending} data-testid="marking-save-draft-btn">
                Save draft
              </Button>
              <Button size="sm" onClick={() => saveAll(true)} disabled={saveMutation.isPending} data-testid="marking-finalize-btn">
                {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Save & finalize
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


// Single row inside the PublishDialog — extracted to satisfy
// react/no-unstable-nested-components.
const PublishOptRow = ({ optKey, label, hint, checked, onChange }) => (
  <label className="flex items-start gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={`publish-opt-${optKey}`} className="mt-0.5" />
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="text-xs text-slate-500">{hint}</p>
    </div>
  </label>
);

// ============================== PUBLISH DIALOG ==============================
const PublishDialog = ({ open, onOpenChange, exam, tenantId, onPublished }) => {
  const existing = exam.publish_options || {};
  const [opts, setOpts] = useState({
    show_marks: existing.show_marks ?? true,
    show_letter_grade: existing.show_letter_grade ?? true,
    show_competency: existing.show_competency ?? true,
    show_feedback: existing.show_feedback ?? true,
    notify_parents: existing.notify_parents ?? true,
  });
  const m = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/publish-results`, { method: 'POST', body: JSON.stringify(opts) }),
    onSuccess: (r) => {
      toast.success(r.notifications_queued
        ? 'Results published — parents are being notified now.'
        : 'Results published — students & parents can now view.');
      onPublished?.();
      onOpenChange(false);
    },
    onError: e => toast.error(e.message),
  });
  const setOpt = (k) => (v) => setOpts(o => ({ ...o, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="publish-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4" /> Publish results</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Choose what students and parents will see on their report cards.</p>
        <div className="space-y-1">
          <PublishOptRow optKey="show_marks" label="Numeric marks" hint="e.g. 78 / 100" checked={opts.show_marks} onChange={setOpt('show_marks')} />
          <PublishOptRow optKey="show_letter_grade" label="Letter grade" hint="e.g. A, B+, C — from the configured grading scale" checked={opts.show_letter_grade} onChange={setOpt('show_letter_grade')} />
          <PublishOptRow optKey="show_competency" label="Competency band" hint="e.g. Exceeding · Developing — if your grading scale defines them" checked={opts.show_competency} onChange={setOpt('show_competency')} />
          <PublishOptRow optKey="show_feedback" label="Per-question feedback" hint="Teacher remarks attached to each answer" checked={opts.show_feedback} onChange={setOpt('show_feedback')} />
          <div className="border-t my-2" />
          <PublishOptRow optKey="notify_parents" label="Notify parents via WhatsApp / SMS" hint="Fan-out one message per linked parent — respects each parent's channel preference and the tenant's notification settings." checked={opts.notify_parents} onChange={setOpt('notify_parents')} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending} data-testid="confirm-publish-btn">
            {m.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
            Publish
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ============================== MAIN PANEL ==============================
const ExamSubmissionsPanel = ({ exam, tenantId, classes = [], subjects = [] }) => {
  const qc = useQueryClient();
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [marking, setMarking] = useState(null); // submission object when viewing
  const [showPublish, setShowPublish] = useState(false);

  const examClasses = useMemo(
    () => (exam.class_ids || []).map(cid => classes.find(c => c.id === cid)).filter(Boolean),
    [exam.class_ids, classes],
  );
  const examSubjects = useMemo(
    () => (exam.subjects || []).map(es => subjects.find(s => s.id === es.subject_id)).filter(Boolean),
    [exam.subjects, subjects],
  );

  const params = new URLSearchParams();
  if (selectedClass) params.set('class_id', selectedClass);
  if (selectedSubject) params.set('subject_id', selectedSubject);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['exam-submissions', exam.id, selectedClass, selectedSubject],
    queryFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/submissions?${params.toString()}`),
    enabled: !!exam.id,
  });
  const submissions = data?.submissions || [];

  // Latest fan-out stats (refreshes every 5s while results are published, so
  // the badge ticks up live as the background worker drains).
  const { data: notifyData } = useQuery({
    queryKey: ['exam-notify-stats', exam.id, exam.results_published],
    queryFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/notify-stats`),
    enabled: !!exam.id && !!exam.results_published,
    refetchInterval: exam.results_published ? 5000 : false,
  });
  const notifyStats = notifyData?.stats || {};

  const autoMarkMutation = useMutation({
    mutationFn: () => api(
      `/tenants/${tenantId}/exams/${exam.id}/auto-mark?class_id=${selectedClass}&subject_id=${selectedSubject}`,
      { method: 'POST' },
    ),
    onSuccess: (d) => {
      toast.success(`Auto-marked ${d.auto_marked} · ${d.awaiting_manual || 0} awaiting manual`);
      refetch();
    },
    onError: e => toast.error(e.message),
  });
  const unpublishMutation = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/exams/${exam.id}/unpublish-results`, { method: 'POST' }),
    onSuccess: () => { toast.success('Results unpublished'); refetch(); qc.invalidateQueries({ queryKey: ['school-exams'] }); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); },
    onError: e => toast.error(e.message),
  });

  const counts = useMemo(() => {
    const c = { total: submissions.length, awaiting: 0, marked: 0 };
    submissions.forEach(s => {
      if (['awaiting_manual', 'partially_marked'].includes(s.marking_status)) c.awaiting++;
      else if (['fully_marked', 'auto_marked', 'moderated'].includes(s.marking_status)) c.marked++;
    });
    return c;
  }, [submissions]);

  // -------- Marking sub-view --------
  if (marking) {
    return (
      <MarkingView
        key={marking.id || marking._id}
        submission={marking}
        exam={exam}
        tenantId={tenantId}
        subjectId={marking.subject_id || selectedSubject}
        onBack={() => setMarking(null)}
        onSaved={() => { refetch(); }}
      />
    );
  }

  return (
    <div className="space-y-4" data-testid="exam-submissions-panel">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between gap-2 flex-wrap">
            <span className="flex items-center gap-2"><ClipboardList className="h-4 w-4" /> Submissions &amp; marking</span>
            <div className="flex items-center gap-2">
              {exam.results_published ? (
                <Button size="sm" variant="outline" onClick={() => unpublishMutation.mutate()}
                        disabled={unpublishMutation.isPending} data-testid="unpublish-results-btn">
                  <EyeOff className="h-3 w-3 mr-1" /> Unpublish
                </Button>
              ) : (
                <Button size="sm" onClick={() => setShowPublish(true)}
                        disabled={counts.marked === 0}
                        data-testid="publish-results-btn">
                  <Send className="h-3 w-3 mr-1" /> Publish results
                </Button>
              )}
            </div>
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <Badge variant="outline">{counts.total} submissions</Badge>
            {counts.awaiting > 0 && <Badge className="bg-amber-100 text-amber-700">{counts.awaiting} awaiting manual</Badge>}
            {counts.marked > 0 && <Badge className="bg-emerald-100 text-emerald-700">{counts.marked} marked</Badge>}
            {exam.results_published && <Badge className="bg-green-100 text-green-800"><Send className="h-3 w-3 mr-1" />Results published</Badge>}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div className="min-w-0 sm:min-w-[160px]">
              <Label className="text-xs">Class</Label>
              <Select value={selectedClass || 'all'} onValueChange={v => setSelectedClass(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="submissions-class-filter"><SelectValue placeholder="All classes" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {examClasses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-0 sm:min-w-[160px]">
              <Label className="text-xs">Subject</Label>
              <Select value={selectedSubject || 'all'} onValueChange={v => setSelectedSubject(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="submissions-subject-filter"><SelectValue placeholder="All subjects" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  {examSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}
                    data-testid="submissions-refresh-btn">
              <RotateCcw className={`h-3 w-3 mr-1 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {selectedClass && selectedSubject && (
              <Button size="sm" variant="outline"
                      onClick={() => autoMarkMutation.mutate()}
                      disabled={autoMarkMutation.isPending}
                      data-testid="run-auto-mark-btn">
                {autoMarkMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1" />}
                Re-run auto-mark
              </Button>
            )}
            {(!selectedClass || !selectedSubject) && (
              <span className="text-[11px] text-slate-400 italic">Pick a class &amp; subject to re-run auto-mark</span>
            )}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-10 text-slate-400" data-testid="submissions-empty">
              <ClipboardList className="h-8 w-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No submissions yet for this exam{selectedClass || selectedSubject ? ' with the chosen filters' : ''}.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Student</TableHead>
                  <TableHead className="hidden md:table-cell">Class / Subject</TableHead>
                  <TableHead className="hidden lg:table-cell">Submitted</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead className="text-right">Grade band</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {submissions.map(s => (
                  <TableRow key={s.id || s._id} data-testid={`submission-row-${s.id || s._id}`}>
                    <TableCell>
                      <p className="font-medium text-sm">{s.student_name || s.student_id}</p>
                      {s.admission_number && <p className="text-xs text-slate-500">{s.admission_number}</p>}
                    </TableCell>
                    <TableCell className="text-xs hidden md:table-cell">
                      {(classes.find(c => c.id === s.class_id) || {}).name || s.class_id || '—'}
                      <br />
                      <span className="text-slate-400">{(subjects.find(sb => sb.id === s.subject_id) || {}).name || s.subject_id || '—'}</span>
                    </TableCell>
                    <TableCell className="text-xs hidden lg:table-cell">
                      {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {s.marks != null ? (
                        <span className="font-bold text-sm">{s.marks}<span className="text-xs text-slate-400"> / {s.max_marks}</span></span>
                      ) : <span className="text-slate-400 text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <GradeBadge grade={s.grade} percentage={s.percentage} competency={s.competency} />
                    </TableCell>
                    <TableCell><StatusBadge status={s.marking_status || s.status} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => setMarking(s)}
                              data-testid={`open-marking-${s.id || s._id}`}>
                        <Pencil className="h-3 w-3 mr-1" /> Mark
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {exam.results_published && exam.publish_options && (
            <div className="rounded border border-green-200 bg-green-50 p-2 text-xs text-green-700 flex items-start gap-2" data-testid="publish-options-summary">
              <Award className="h-3 w-3 mt-0.5 shrink-0" />
              <div className="flex-1">
                <strong>Published view:</strong>{' '}
                {[
                  exam.publish_options.show_marks && 'marks',
                  exam.publish_options.show_letter_grade && 'letter grade',
                  exam.publish_options.show_competency && 'competency',
                  exam.publish_options.show_feedback && 'feedback',
                ].filter(Boolean).join(' · ') || 'nothing visible (consider unpublishing)'}
                {(notifyStats.notified > 0 || notifyStats.skipped > 0 || notifyStats.failed > 0) && (
                  <div className="mt-1 flex items-center gap-2 text-[11px]" data-testid="notify-stats-line">
                    <Send className="h-3 w-3" />
                    <span>Parent notifications: <strong>{notifyStats.notified || 0}</strong> sent</span>
                    {notifyStats.skipped > 0 && <span className="text-amber-600">· {notifyStats.skipped} skipped (no channel / opted out)</span>}
                    {notifyStats.failed > 0 && <span className="text-red-600">· {notifyStats.failed} failed</span>}
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <PublishDialog
        open={showPublish}
        onOpenChange={setShowPublish}
        exam={exam}
        tenantId={tenantId}
        onPublished={() => { refetch(); qc.invalidateQueries({ queryKey: ['school-exams'] }); qc.invalidateQueries({ queryKey: ['school-exam-detail'] }); }}
      />
    </div>
  );
};

export default ExamSubmissionsPanel;
